import { Component, computed, effect, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { RouterOutlet } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventType } from '@azure/msal-browser';
import { RouterStore } from '@ct/data';
import { getNavigation, NavigationComponent } from '@ct/navigation';
import { filter, map } from 'rxjs';
import { environment } from '../environments/environment';
import { UserStore } from './store/user.store';

@Component({
  selector: 'ct-root',
  template: ` <ct-navigation
      [hideReleaseNotes]="true"
      (logout)="logout()"
      [navigation]="navigation()"
      (navigate)="navigate($event)"
      [user]="user"
    >
    </ct-navigation>
    <div class="app-wrapper">
      <router-outlet></router-outlet>
    </div>`,
  styleUrl: './app.component.scss',
  imports: [RouterOutlet, NavigationComponent],
})
export class AppComponent implements OnInit {
  private readonly userStore = inject(UserStore);
  private readonly routerStore = inject(RouterStore);
  private readonly iconService = inject(MatIconRegistry);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly msal = inject(MsalService);

  // Convert MSAL authentication events to a signal
  private readonly authEvent = toSignal(
    this.msalBroadcastService.msalSubject$.pipe(
      filter((event) => event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
      map(() => this.msal.instance.getAllAccounts()[0]),
    ),
  );

  // User reacts to msal event + provides a default value
  public user = computed(() => {
    const authenticatedUser = this.authEvent();
    return authenticatedUser ? authenticatedUser : this.msal.instance.getAllAccounts()[0];
  });

  // React to authentication events to set user to store
  private onUserChange = effect(() => {
    const authenticatedUser = this.user();
    if (authenticatedUser) {
      const roles = (authenticatedUser.idTokenClaims?.['roles'] as string[]) || [];
      const isAdmin = roles.includes('ITSupport');

      this.userStore.setUser({ name: authenticatedUser.name || '', isAdmin, roles });
    }
  });

  public navigation = computed(() => {
    const roles = this.userStore.visibleMenuItems();
    return getNavigation('admin', roles as any, !environment.production).filter((nav) =>
      roles.includes('admin') ? true : !nav.adminOnly,
    );
  });

  logout() {
    this.msal.logoutRedirect();
  }

  navigate(path: string[]) {
    this.routerStore.navigate({ commands: path, extras: { queryParams: {} } });
  }

  ngOnInit() {
    this.iconService.addSvgIcon('tnt', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/tnt.svg'));
  }
}
